/**
 * Cirqle Collection Page JavaScript
 * Handles cart operations, filters, and variant selection
 */

class CirqleCollection {
  constructor() {
    this.cartDrawer = document.querySelector('.cart-drawer');
    this.cartOverlay = document.querySelector('.cart-drawer-overlay');
    this.cartBadge = document.querySelector('.cart-badge');

    this.init();
  }

  init() {
    this.setupQuickAdd();
    this.setupCartDrawer();
    this.setupFilters();
    this.setupSwatches();
  }

  // Quick Add to Cart
  setupQuickAdd() {
    document.querySelectorAll('.quick-add').forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId = button.dataset.variantId;
        const originalText = button.textContent;

        button.textContent = 'Adding...';
        button.disabled = true;

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });

          if (!response.ok) throw new Error('Failed to add to cart');

          button.textContent = 'Added!';

          // Update cart count
          await this.updateCartCount();

          // Open cart drawer
          setTimeout(() => {
            this.openCartDrawer();
            button.textContent = originalText;
            button.disabled = false;
          }, 500);

        } catch (error) {
          console.error('Error adding to cart:', error);
          button.textContent = 'Error';
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        }
      });
    });
  }

  // Cart Drawer Management
  setupCartDrawer() {
    // Close button
    const closeBtn = document.querySelector('.cart-drawer-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeCartDrawer());
    }

    // Overlay click
    if (this.cartOverlay) {
      this.cartOverlay.addEventListener('click', () => this.closeCartDrawer());
    }

    // Cart badge click to open drawer
    if (this.cartBadge) {
      this.cartBadge.addEventListener('click', (e) => {
        e.preventDefault();
        this.openCartDrawer();
      });
    }
  }

  async openCartDrawer() {
    await this.loadCartItems();
    this.cartDrawer?.classList.add('active');
    this.cartOverlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeCartDrawer() {
    this.cartDrawer?.classList.remove('active');
    this.cartOverlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  async loadCartItems() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const itemsContainer = document.querySelector('.cart-drawer-items');
      if (!itemsContainer) return;

      if (cart.items.length === 0) {
        itemsContainer.innerHTML = '<div class="cart-empty">Your cart is empty</div>';
        this.updateCartSubtotal(0);
        return;
      }

      itemsContainer.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-key="${item.key}">
          <img src="${item.image}" alt="${item.title}" class="cart-item-image">
          <div class="cart-item-details">
            <div class="cart-item-name">${item.product_title}</div>
            ${item.variant_title ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
            <div class="cart-item-quantity">
              <button class="cart-qty-btn cart-qty-minus" data-key="${item.key}">−</button>
              <span class="cart-qty-value">${item.quantity}</span>
              <button class="cart-qty-btn cart-qty-plus" data-key="${item.key}">+</button>
            </div>
            <button class="cart-item-remove" data-key="${item.key}">Remove</button>
          </div>
          <div class="cart-item-price">${this.formatMoney(item.line_price)}</div>
        </div>
      `).join('');

      this.updateCartSubtotal(cart.total_price);
      this.setupCartItemControls();

    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }

  setupCartItemControls() {
    // Quantity controls
    document.querySelectorAll('.cart-qty-plus').forEach(btn => {
      btn.addEventListener('click', () => this.updateQuantity(btn.dataset.key, 1));
    });

    document.querySelectorAll('.cart-qty-minus').forEach(btn => {
      btn.addEventListener('click', () => this.updateQuantity(btn.dataset.key, -1));
    });

    // Remove buttons
    document.querySelectorAll('.cart-item-remove').forEach(btn => {
      btn.addEventListener('click', () => this.removeItem(btn.dataset.key));
    });
  }

  async updateQuantity(key, change) {
    try {
      const item = document.querySelector(`[data-key="${key}"]`);
      const currentQty = parseInt(item.querySelector('.cart-qty-value').textContent);
      const newQty = currentQty + change;

      if (newQty < 1) {
        await this.removeItem(key);
        return;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: newQty
        })
      });

      if (!response.ok) throw new Error('Failed to update quantity');

      await this.loadCartItems();
      await this.updateCartCount();

    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  async removeItem(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (!response.ok) throw new Error('Failed to remove item');

      await this.loadCartItems();
      await this.updateCartCount();

    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (this.cartBadge) {
        this.cartBadge.textContent = `Cart · ${cart.item_count}`;
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  updateCartSubtotal(total) {
    const subtotalElement = document.querySelector('.cart-subtotal-amount');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(total);
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  }

  // Filter Pills
  setupFilters() {
    const filterPills = document.querySelectorAll('.filter-pill');
    const productCards = document.querySelectorAll('.product-card');

    filterPills.forEach(pill => {
      pill.addEventListener('click', () => {
        // Update active state
        filterPills.forEach(p => p.classList.remove('active'));
        pill.classList.add('active');

        const filterTag = pill.dataset.tag;

        // Filter products
        productCards.forEach(card => {
          const productTags = card.dataset.tags ? card.dataset.tags.split(',') : [];

          if (!filterTag || filterTag === '' || productTags.includes(filterTag)) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });
      });
    });
  }

  // Color Swatches
  setupSwatches() {
    document.querySelectorAll('.swatch').forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const card = swatch.closest('.product-card');
        const variantId = swatch.dataset.variantId;
        const variantImage = swatch.dataset.image;
        const variantPrice = swatch.dataset.price;

        // Update active swatch
        card.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');

        // Update product image
        if (variantImage) {
          const imgFront = card.querySelector('.img-front');
          if (imgFront) {
            imgFront.src = variantImage;
          }
        }

        // Update price
        if (variantPrice) {
          const priceElement = card.querySelector('.product-price');
          if (priceElement) {
            priceElement.textContent = variantPrice;
          }
        }

        // Update quick add button variant
        const quickAddBtn = card.querySelector('.quick-add');
        if (quickAddBtn && variantId) {
          quickAddBtn.dataset.variantId = variantId;
        }
      });
    });
  }
}

// // Initialize when DOM is ready
// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', () => {
//     new CirqleCollection();
//   });
// } else {
//   new CirqleCollection();
// }


// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cirqleCollection = new CirqleCollection();  // ✅ global banao
  });
} else {
  window.cirqleCollection = new CirqleCollection();    // ✅ global banao
}